import { Vm } from './vm'
import { envTypes } from './env-types'
import { parse, Token } from './lang'
import type { Buffers } from './backend'
import { compile, CompileError, CompilePtrs, CompileResult } from './compiler'
import { BLOCK_SIZE, INS, MEM_PAGES, OUTS } from './constants'
import { differ } from './differ'
import { Gen, Info, produce, VNode } from './producer'
import { capitalize } from './util'
import { envLookupMethod } from './env-info'

// TODO: use heuristics to determine if we need a separate copy for each generator
const nonCopyable = ['daverb', 'delay', 'tap', 'sample', 'freesound', 'say']

export interface CtrlPayload {
  ins: [number, number]
  outs: [number, number]
  mems: [Block | Float32Array, Block | Float32Array][]
  memsAfter: [Block | Float32Array, Block | Float32Array][]
  memsReset: [Block | Float32Array, Block | Float32Array][]
}

export interface Build extends CompileResult {
  ins: Block[]
  outs: Block[]
  gens: GenRuntime[]
  ptrs: CompilePtrs
  info: Info
  payload: CtrlPayload
  skip: boolean
}

export class GenRuntime {
  ptr: number
  mem: Uint32Array
  memf32: Float32Array
  floats?: Float32Array
  initial?: Float32Array

  get kind() {
    return this.gen.kind
  }

  getId() {
    const getId = envLookupMethod(this.kind, 'get_id')!
    return getId ? this.frontend.vm.instance[getId](this.ptr) as number : null
  }

  constructor(
    private frontend: Frontend,
    public gen: Gen,
    public out: Block | null
  ) {
    const { vm, engine, gensFree, gensUsed, initials } = frontend

    this.ptr = vm.instance[`gen_${this.kind}_constructor`](
      0,
      engine,
      vm.Rate.Audio
    )

    const kind = gen.kind

    const getFloats = envLookupMethod(this.kind, 'get_floats')
    if (getFloats && getFloats in vm.instance) {
      const ptr = vm.instance[getFloats](this.ptr)
      this.floats = vm.view.getF32(ptr, 1 << 16)
    }

    // first time creating this generator kind
    if (!(kind in gensUsed)) {
      // store initial object memory
      const getObjectSize = `util_getObjectSize__gen_${this.kind}_${capitalize(this.kind)}_`
      const size = vm.instance[getObjectSize]() >> 2
      initials[kind] = new Float32Array(new SharedArrayBuffer(size << 2))
      initials[kind].set(vm.view.getF32(this.ptr, size))

      // create pool
      gensFree[kind] = []
      gensUsed[kind] = []
    }

    if (nonCopyable.includes(kind)) {
      const getObjectSize = `util_getObjectSize__gen_${this.kind}_${capitalize(this.kind)}_`
      const size = vm.instance[getObjectSize]() >> 2

      this.initial = new Float32Array(new SharedArrayBuffer(size << 2))
      this.initial.set(vm.view.getF32(this.ptr, size))
    }

    this.mem = vm.view.getU32(this.ptr, initials[kind].length)
    this.memf32 = vm.view.getF32(this.ptr, initials[kind].length)

    gensUsed[kind].push(this)

    gen.runtime = this
  }

  copyInto(other: GenRuntime) {
    other.mem.set(this.mem)
  }

  reset() {
    this.memf32.set(this.initial ?? this.frontend.initials[this.kind])
  }
}

export type Block = Float32Array & { index: number }

export class Frontend {
  engine: number
  buffers: Buffers
  literals: [Block, Block]
  literalsCurrent = 0
  rms: Block
  lists: Block
  scalars: Block

  initials: Record<string, Float32Array> = {}

  gensUsed: Record<string, GenRuntime[]> = {}
  gensFree: Record<string, GenRuntime[]> = {}

  outsUsed: Block[] = []
  outsFree: Block[]

  running = false

  declare createBlock: (index?: number, length?: number) => Block

  get active() {
    return this.builds.at(-1)
  }

  constructor(public vm: Vm, blockSize = BLOCK_SIZE) {
    this.engine = vm.exports.engine_constructor(0, 44100, 64)

    this.createBlock = (index = -1, length = blockSize): Block =>
      Object.assign(
        vm.view.getF32(
          vm.exports.engine_createBlock(
            this.engine,
            length
          ),
          length
        ),
        { index }
      )

    this.buffers = {
      ringPosBuffer: vm.view.getU32(vm.exports.index_getRingPos(), 1),
      ins: Array.from({ length: INS }, (_, i) => this.createBlock(i)),
      outs: Array.from({ length: OUTS }, (_, i) => this.createBlock(i)),
    }

    this.rms = this.createBlock()

    this.literals = [
      this.createBlock(),
      this.createBlock(),
    ]

    this.lists = this.createBlock()

    this.scalars = this.createBlock()

    this.outsFree = [...this.buffers.outs]
  }

  freeOutput = (out: Block) => {
    const index = this.outsUsed.indexOf(out)
    if (!~index) {
      throw new Error('Attempted to free output that is not used.')
    }
    this.outsUsed.splice(index, 1)
    this.outsFree.unshift(out)
    out.fill(0)
  }

  getOutput = () => {
    const out = this.outsFree.shift()
    if (!out) {
      throw new Error('No outputs left.')
    }
    this.outsUsed.push(out)
    return out
  }

  freeGen = (gen: GenRuntime) => {
    if (!(gen.kind in this.gensUsed)) {
      throw new Error(`Attempted to free gen of unknown kind '${gen.kind}'.`)
    }
    const index = this.gensUsed[gen.kind].indexOf(gen)
    if (!~index) {
      throw new Error('Attempted to free gen that is not used.')
    }
    this.gensUsed[gen.kind].splice(index, 1)
    this.gensFree[gen.kind].push(gen)
    gen.reset()
  }

  createGen(gen: Gen, out: Block | null) {
    if (gen.kind in this.gensFree && this.gensFree[gen.kind].length) {
      const genRuntime = this.gensFree[gen.kind].pop()!
      gen.runtime = genRuntime
      genRuntime.out = out
      this.gensUsed[gen.kind].push(genRuntime)
      return genRuntime
    }
    return new GenRuntime(this, gen, out)
  }

  setTokens(tokens: Token[]) {
    const cst = parse(tokens)
    return produce(cst)
  }

  getPtrs(ins: Block[], outs: Block[], gens: GenRuntime[]) {
    this.literalsCurrent = 1 - this.literalsCurrent
    return {
      engine: this.engine,
      ins: ins.map((b) => b.byteOffset),
      outs: outs.map((b) => b.byteOffset),
      gens: gens.map((g) => g.ptr),
      literals: this.literals[this.literalsCurrent].byteOffset,
      lists: this.lists.byteOffset,
      scalars: this.scalars.byteOffset
    }
  }

  debug = false

  free({ gens, outs }: { gens: Build['gens'], outs: Build['outs'] }) {
    outs.forEach(this.freeOutput)
    gens.forEach(this.freeGen)
    this.debug && console.log('free outs:', this.outsFree.length)
  }

  builds: Build[] = []

  allocateMemories(info: Info) {
    if (info.outs > this.outsFree.length) {
      console.log(info)
      throw new Error('No outputs left.')
    }

    for (const { kind } of info.scope.gens.values()) {
      const id = `gen_${kind}_constructor`
      if (!(id in this.vm.instance)) {
        console.log(this.vm.instance)
        throw new Error(`No such gen "${kind}" exists.`)
      }
    }

    const ins = Array.from({ length: info.ins },
      (_, i) => this.buffers.ins[i]
    )

    const outs = Array.from({ length: info.outs },
      () => this.getOutput()
    )

    const gens: GenRuntime[] = Array.from(info.scope.gens.values()).map(
      (gen) =>
        this.createGen(gen, gen.audio ? outs[gen.audio.ptr] : null)
    )

    // we need the out to pick it up in the ui
    info.scope.audios.forEach((audio) => {
      audio.floats = outs[audio.ptr]
    })

    const ptrs = this.getPtrs(ins, outs, gens)

    return { ins, outs, gens, ptrs }
  }

  async build(info: Info): Promise<CompileError | Build> {
    const prev = this.active
    const prevInfo = prev?.info

    const serializeNodes = (nodes: Set<VNode> | VNode[] | undefined) => {
      return [...(nodes ?? [])]
        .filter((x) =>
          !['number', 'string', 'special'].includes(x.type)
          && !(x.type === 'op' && x.text === '\\')
        )
        .map((x) => x.text).join(' ')
    }

    const gensDiff = differ(
      prevInfo
        ? new Set(prevInfo.scope.gens.values())
        : null,
      new Set(info.scope.gens.values()),
      false,
      (gen) => `${gen.kind}${serializeNodes(gen.audio?.callers.flatMap(x => x.tokens))}`
    )

    // console.log({
    //   gensDiff
    // })

    // console.log(this.literals[this.literalsCurrent])

    const isTopEqual =
      serializeNodes(info.scope.nodes)
      === serializeNodes(prevInfo?.scope.nodes)

    const isInfoSimilar = prevInfo
      && (
        prevInfo.outs === info.outs
        && prevInfo.scope.audios.size === info.scope.audios.size
      )

    if (isTopEqual && isInfoSimilar && prev) {
      const literalsDiff = differ(
        prevInfo
          ? new Set(prevInfo.scope.literals.values())
          : null,
        new Set(info.scope.literals.values()),
        true,
        (literal) => `${literal.value}`
      )

      // const paramsDiff = differ(
      //   prevInfo
      //     ? new Set(prevInfo.scope.params.values())
      //     : null,
      //   new Set(info.scope.params.values()),
      //   true,
      //   (param) => `${param.name?.text ?? ''},${param.solo ? 1 : 0},${param.mute ? 1 : 0}`
      // )

      // console.log({
      //   literalsDiff
      // })

      if (
        literalsDiff.added.length
        + literalsDiff.removed.length
        + gensDiff.added.length
        + gensDiff.changed.length
        + gensDiff.removed.length
        === 0
      ) {
        literalsDiff.changed.forEach(([, n]) => {
          this.literals[this.literalsCurrent][n.ptr] = n.value
        })

        literalsDiff.moved.forEach(([, n]) => {
          this.literals[this.literalsCurrent][n.ptr] = n.value
        })

        const namedParams = [...info.scope.params].filter(([, param]) => param.name)
        let hasSolo = false
        for (const [, param] of namedParams) {
          if (param.solo && param.name?.text !== "'main'") {
            hasSolo = true
          }
        }
        for (const [literal, param] of namedParams) {
          this.literals[this.literalsCurrent][literal.ptr] =
            (param.solo || !hasSolo || param.name?.text === "'main'")
              && !param.mute
              ? literal.value
              : 0
        }

        const build: Build = this.builds[this.builds.length - 1] = {
          ...prev,
          info,
          skip: true
        }

        gensDiff.equal.forEach(([p, n]) => {
          p.audio?.callers.forEach((caller, i) => {
            const na = n.audio?.callers[i]
            if (na) {
              caller.line = na.line
              caller.col = na.col
              caller.right = na.right
            }
          })
          n.audio = p.audio!
          n.runtime = p.runtime!
        })

        const plist = [...prev.info.scope.audios]
        const nlist = [...info.scope.audios]
        plist.forEach((p, i) => {
          const n = nlist[i]
          n.floats = p.floats!
          p.callers.forEach((view, i) => {
            n.callers[i].ref = view.ref
            n.callers[i].key = view.key
          })
        })

        return build
      }
    }

    const { ins, outs, gens, ptrs } = this.allocateMemories(info)

    const code = info.scope.emitter.code.join('\n')
      + ' rms_ '
      + ' engine_get_ratesSamples '

    // console.log(code)

    if (this.debug) {
      console.groupCollapsed('info')
      console.log(info)
      console.groupEnd()
    }

    const envTypesShaked = Object.fromEntries(Object.entries(envTypes)
      .filter(([key, fn]) =>
        code.includes(key)
      ))

    // console.log(envTypesShaked)
    // return

    const res = await compile(info, {
      envTypes: envTypesShaked,
      ...ptrs
    }, this.debug)

    if ('error' in res) {
      this.free({ outs, gens })
      return res
    }

    const mems: [Float32Array, Float32Array][] = []

    gensDiff.equal.concat(gensDiff.moved).forEach(([p, n]) => {
      if (!nonCopyable.includes(p.kind)) {
        mems.push([p.runtime!.memf32, n.runtime!.memf32])
      }
      if (p.runtime!.out != null) mems.push([p.runtime!.out!, n.runtime!.out!])
    })

    const memsAfter: [Float32Array, Float32Array][] = []

    gensDiff.removed.forEach((g) => {
      memsAfter.push([g.runtime!.initial ?? this.initials[g.kind], g.runtime!.memf32])
    })

    const memsReset: [Float32Array, Float32Array][] = []

    gens.forEach((g) => {
      // if (!nonCopyable.includes(g.kind)) {
      memsReset.push([g.initial ?? this.initials[g.kind], g.memf32])
      // }
    })

    const payload: CtrlPayload = {
      ins: [
        ins[0].index,
        ins[1].index,
      ],
      outs: [
        outs[info.L].index,
        outs[info.R].index,
      ],
      mems,
      memsAfter,
      memsReset,
    }

    const b: Build = { ...res, info, ins, outs, gens, ptrs, payload, skip: false }

    // console.groupCollapsed('payload')
    // console.log(payload)
    // console.groupEnd()

    this.builds.push(b)
    if (this.builds.length > 2) {
      this.free(this.builds.shift()!)
    }

    return b
  }

  // TODO: consolidate with memReset
  getResetMems() {
    return [...this.active!.info.scope.gens.values()].map((gen) => [
      gen.runtime!.memf32,
      gen.runtime!.initial ?? this.initials[gen.kind]
    ] as [Float32Array, Float32Array])
  }

  reset() {
    const build = this.active
    if (!build) return

    // if (this.running) {
    return this.getResetMems()
    // }
    // else {
    //   if (build) {
    //     [...build.info.scope.gens.values()].forEach((gen) => gen.runtime!.reset())
    //   }
    // }
  }
}

export function createMemory() {
  return new WebAssembly.Memory({
    initial: MEM_PAGES,
    maximum: MEM_PAGES,
    shared: true
  })
}
